import {createContext, useContext, FC, ReactNode} from 'react';
import {InterpreterFrom} from 'xstate';
import {useInterpret, useSelector} from '@xstate/react';
import {trackMachine} from '../machines/trackMachine';
import {generateSelector} from '../utils/generateSelector';

export type TrackMachine = typeof trackMachine;
export type TrackService = InterpreterFrom<TrackMachine>;
export type TrackSend = TrackService['send'];

interface TrackStateContextType {
  trackService: TrackService;
}

const AudioTrackStateContext = createContext<TrackStateContextType>(
  {} as TrackStateContextType
);

const SubtitleTrackStateContext = createContext<TrackStateContextType>(
  {} as TrackStateContextType
);

interface TrackStateProviderProps {
  children: ReactNode;
}

export const TrackStateProvider: FC<TrackStateProviderProps> = ({children}) => {
  const audioTrackService = useInterpret(trackMachine);
  const subtitleTrackService = useInterpret(trackMachine);

  return (
    <AudioTrackStateContext.Provider value={{trackService: audioTrackService}}>
      <SubtitleTrackStateContext.Provider
        value={{trackService: subtitleTrackService}}
      >
        {children}
      </SubtitleTrackStateContext.Provider>
    </AudioTrackStateContext.Provider>
  );
};

export const useAudioTrackService = (): TrackService => {
  return useContext(AudioTrackStateContext).trackService;
};

export const useSubtitleTrackService = (): TrackService => {
  return useContext(SubtitleTrackStateContext).trackService;
};

export const useIsOpen = (service: TrackService): boolean => {
  return useSelector(service, generateSelector<TrackMachine>('open'));
};

export const useAudioPanelIsOpen = (): boolean => {
  const service = useAudioTrackService();
  return useIsOpen(service);
};

export const useSubtitlePanelIsOpen = (): boolean => {
  const service = useSubtitleTrackService();
  return useIsOpen(service);
};
